import { Badge, Box, Tabs, Text } from '@mantine/core';
import { MessagesSquare, Tags } from 'lucide-react';
import type { ReactElement } from 'react';
import DetailDrawer from '@/design/DetailDrawer';
import KeyValueTable from '@/design/KeyValueTable';
import StatTile from '@/design/StatTile';
import type { VizAccent } from '@/design/VizAccents';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { formatCount, formatPercent, formatTimestamp, humanUser } from '@/shared/analytics/MetricFormatting';
import { recordDrawerExtensions } from './RecordDrawerExtensions';
import ConversationViewer from './ConversationViewer';

interface InteractionRecordDrawerProps {
    record: InteractionRecord | null;
    onClose: () => void;
    accent: VizAccent;
}

const DIMENSION_ORDER = ['difficulty', 'task_category', 'task_subcategory', 'work_related', 'user_department', 'user_title', 'user_org_unit', 'user_manager'];

/** The classified value of a dimension on a record, or an em-dash when absent. */
export function dimValue(record: InteractionRecord, key: string): string {
    return record.dimensions[key]?.value ?? '—';
}

/** Mantine badge color for a difficulty-style classification value. */
export function difficultyColor(value: string): string {
    switch (value.toLowerCase()) {
        case 'trivial':
        case 'low':
        case 'easy':
            return 'teal';
        case 'medium':
        case 'moderate':
            return 'yellow';
        case 'high':
        case 'hard':
        case 'complex':
            return 'red';
        default:
            return 'gray';
    }
}

/** A dimension's classifier confidence formatted as a percent, or an em-dash. */
export function confidenceOf(record: InteractionRecord, key: string): string {
    const confidence = record.dimensions[key]?.confidence;
    return confidence === null || confidence === undefined ? '—' : formatPercent(confidence);
}

function orderedDimensionKeys(record: InteractionRecord): string[] {
    const known = DIMENSION_ORDER.filter((key) => record.dimensions[key] !== undefined);
    const extras = Object.keys(record.dimensions).filter((key) => !DIMENSION_ORDER.includes(key));
    return [...known, ...extras];
}

function drawerEntries(record: InteractionRecord): [string, unknown][] {
    const base: [string, unknown][] = [
        ['session', record.sessionId],
        ['model', record.model],
        ['input tokens', formatCount(record.inputTokens)],
        ['output tokens', formatCount(record.outputTokens)],
        ['prompt / seq', `${record.turnIndex} / ${record.sequenceNumber}`],
        ['session topic', record.sessionTopic ?? '—'],
        ['topic label', record.topicLabel ?? '—'],
        ['sensitivity', record.sensitivityFlags.length === 0 ? 'none' : record.sensitivityFlags.join(', ')],
        ['classifier', record.classifierStateId ?? '—'],
    ];
    const dims: [string, unknown][] = orderedDimensionKeys(record).map((key) => {
        const dim = record.dimensions[key];
        if (dim === undefined) return [key, '—'];
        const confidence = dim.confidence === null ? '' : ` · ${formatPercent(dim.confidence)}`;
        return [key, `${dim.value}${confidence} · ${dim.method}`];
    });
    return [...base, ...dims];
}

/** Right-side drawer for one classified interaction turn: session conversation plus the full classification record. */
export default function InteractionRecordDrawer({ record, onClose, accent }: InteractionRecordDrawerProps): ReactElement {
    return (
        <DetailDrawer
            opened={record !== null}
            onClose={onClose}
            size={680}
            scrollBody={false}
            avatarSource={record?.userId ?? ''}
            title={record === null ? '' : (record.topicLabel ?? record.sessionTopic ?? 'Prompt')}
            subtitle={record === null ? '' : `${humanUser(record.userId)} · ${formatTimestamp(record.timestamp)}`}
            stats={
                record === null ? undefined : (
                    <>
                        <StatTile label="Model">
                            <Text size="xs" fw={700} ff="monospace">
                                {record.model}
                            </Text>
                        </StatTile>
                        <StatTile label="Total tokens">
                            <Text size="xs" fw={700} ff="monospace">
                                {formatCount(record.inputTokens + record.outputTokens)}
                            </Text>
                        </StatTile>
                        <StatTile label="Difficulty">
                            <Badge size="xs" radius="sm" variant="light" color={difficultyColor(dimValue(record, 'difficulty'))}>
                                {dimValue(record, 'difficulty')}
                            </Badge>
                        </StatTile>
                    </>
                )
            }
        >
            {record !== null && (
                <Tabs defaultValue="conversation" keepMounted={false} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Tabs.List mx="lg" pt="md" style={{ flexShrink: 0 }}>
                        <Tabs.Tab value="conversation" leftSection={<MessagesSquare size={14} />}>
                            Conversation
                        </Tabs.Tab>
                        <Tabs.Tab value="classification" leftSection={<Tags size={14} />}>
                            Classification
                        </Tabs.Tab>
                        {recordDrawerExtensions().map((extension) => {
                            const TabIcon = extension.icon;
                            return (
                                <Tabs.Tab key={extension.label} value={extension.label} leftSection={TabIcon === undefined ? undefined : <TabIcon size={14} />}>
                                    {extension.label}
                                </Tabs.Tab>
                            );
                        })}
                    </Tabs.List>
                    <Box p="lg" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <Tabs.Panel value="conversation">
                            <ConversationViewer sessionId={record.sessionId} focusTurnIndex={record.turnIndex} accent={accent} />
                        </Tabs.Panel>
                        <Tabs.Panel value="classification">
                            <KeyValueTable entries={drawerEntries(record)} keyWidth="38%" />
                        </Tabs.Panel>
                        {recordDrawerExtensions().map((extension) => (
                            <Tabs.Panel key={extension.label} value={extension.label}>
                                <extension.component record={record} />
                            </Tabs.Panel>
                        ))}
                    </Box>
                </Tabs>
            )}
        </DetailDrawer>
    );
}
